// Administration › Tickets (admin:tickets): every ticket people send from Settings (packages/shared
// domain/ticket.ts), the open ones first in view. Accept or deny each one, with a reply if you like: the
// person who sent it is told on the bell and by email, and sees the answer in Settings. A decision can be
// changed (the sender is told again), or the ticket put back to open. A ticket the sender withdrew stays
// listed, but there's nothing to decide on it. Screenshots show under the details and open full size.
import { useMemo, useState } from 'react';
import {
  TICKET_CATEGORY_LABEL, TICKET_PRIORITY_LABEL, TICKET_STATUS_LABEL, type Ticket, type TicketStatus,
} from '@technohull/shared';
import { useAllTickets, useDecideTicket } from '../../features/tickets.js';
import { useNameResolver } from '../../features/directory.js';
import { TicketStatusPill } from '../../components/Tickets.js';
import { TicketShots } from '../../components/TicketShots.js';
import { Chip, ChipGroup, EmptyState, ErrorState, FilterBar, InlineError, LoadingState, PageHeader, Pill, SearchInput, SyncLabel } from '../../components/ui.js';
import { useDateFormatter } from '../../lib/format.js';
import { useTabState } from '../../lib/tabState.js';
import { fold, hasText } from '../../lib/search.js';
import { AdminBack } from './AdminHub.js';

type Filter = TicketStatus | 'all';
const FILTERS: Filter[] = ['open', 'accepted', 'denied', 'withdrawn', 'all'];

export function AdminTickets() {
  const { data, isLoading, error, refetch, isFetching } = useAllTickets();
  const decide = useDecideTicket();
  const nameOf = useNameResolver();
  const df = useDateFormatter();
  const [filter, setFilter] = useTabState<Filter>('tickets.filter', 'open');
  const [q, setQ] = useState('');
  // the ticket being answered (or re-answered), and the reply typed for it
  const [answering, setAnswering] = useState<number | null>(null);
  const [reply, setReply] = useState('');

  const tickets = useMemo(() => data?.tickets ?? [], [data]);
  const counts = useMemo(() => {
    const c: Record<Filter, number> = { open: 0, accepted: 0, denied: 0, withdrawn: 0, all: tickets.length };
    for (const t of tickets) c[t.status]++;
    return c;
  }, [tickets]);
  const shown = useMemo(() => {
    const query = fold(q.trim());
    return tickets
      .filter((t) => filter === 'all' || t.status === filter)
      .filter((t) => hasText(query, `${t.ref} ${t.subject} ${t.details} ${t.page} ${t.author} ${nameOf(t.author)}`))
      // the urgent open ones first, then newest first
      .sort((a, b) => Number(b.status === 'open' && b.priority === 'urgent') - Number(a.status === 'open' && a.priority === 'urgent') || b.id - a.id);
  }, [tickets, filter, q, nameOf]);

  const startAnswer = (t: Ticket) => { decide.reset(); setAnswering(t.id); setReply(t.reply); };
  const send = (t: Ticket, decision: 'accepted' | 'denied' | 'open') => {
    decide.mutate(
      { id: t.id, decision, ...(decision === 'open' ? {} : { reply: reply.trim() }) },
      { onSuccess: () => setAnswering(null) },
    );
  };

  return (
    <>
      <AdminBack />
      <PageHeader title="Tickets">
        <button className="btn btn-sm" disabled={isFetching} onClick={() => void refetch()}><SyncLabel busy={isFetching} idle="Refresh" busyText="Refreshing…" /></button>
      </PageHeader>

      <FilterBar search={<SearchInput value={q} onChange={setQ} placeholder="Search tickets" width={220} />}>
        <ChipGroup label="Show">
          {FILTERS.map((f) => (
            <Chip key={f} active={filter === f} onClick={() => setFilter(f)}>
              {f === 'all' ? 'All' : TICKET_STATUS_LABEL[f]} ({counts[f]})
            </Chip>
          ))}
        </ChipGroup>
      </FilterBar>

      {isLoading && <LoadingState label="Loading tickets…" />}
      {error && <ErrorState error={error} onRetry={() => void refetch()} />}
      {data && shown.length === 0 && (
        <EmptyState
          title={q.trim() ? 'No ticket matches' : filter === 'open' ? 'No open tickets' : filter === 'all' ? 'No tickets yet' : `No ${TICKET_STATUS_LABEL[filter].toLowerCase()} tickets`}
          hint={q.trim() ? 'Try other words, or show all tickets.' : filter === 'open' ? 'Every ticket has an answer.' : 'People send tickets from Settings, or the Tickets item in their menu.'}
        />
      )}

      <div className="sr-list tk-admin">
        {shown.map((t) => {
          const who = nameOf(t.author) || t.author;
          const isAnswering = answering === t.id;
          const busy = isAnswering && decide.isPending;
          return (
            <article key={t.id} className={`sr-card tk-card is-${t.status}`} aria-label={`${t.ref}: ${t.subject}`}>
              <div className="sr-top">
                <span className="sr-ref">{t.ref}</span>
                <TicketStatusPill status={t.status} />
                {t.priority !== 'normal' && <Pill kind={t.priority === 'urgent' ? 'warn' : ''}>{TICKET_PRIORITY_LABEL[t.priority]}</Pill>}
                <span className="sr-when">{df.dateTime(t.createdAt, { year: 'auto' })}</span>
              </div>
              <div className="sr-vessel">{t.subject}</div>
              <div className="sr-grid">
                <div><span className="sr-k">From</span>{who}{who !== t.author && <span className="muted"> · {t.author}</span>}</div>
                <div><span className="sr-k">About</span>{TICKET_CATEGORY_LABEL[t.category]}</div>
                {t.page && <div><span className="sr-k">Page</span>{t.page}</div>}
              </div>
              {t.details && <div className="sr-details"><span className="sr-k">Details</span><span>{t.details}</span></div>}
              <TicketShots ticket={t} />

              {t.status === 'withdrawn' ? (
                <div className="sr-decided muted">Withdrawn by {who} · {df.dateTime(t.updatedAt, { year: 'auto' })}</div>
              ) : isAnswering ? (
                <div className="sr-compose">
                  <label className="sr-field">
                    <span className="field-label">Reply to {who} <span className="muted">(optional; they see it in Settings and in the email)</span></span>
                    <textarea className="field tk-reply-in" rows={3} maxLength={2000} value={reply} autoFocus onChange={(e) => setReply(e.target.value)} />
                  </label>
                  {decide.isError && <InlineError error={decide.error} onDismiss={() => decide.reset()} />}
                  <div className="sr-compose-actions">
                    <button className="btn btn-sm btn-primary" disabled={busy} onClick={() => send(t, 'accepted')}>{busy && decide.variables?.decision === 'accepted' ? 'Saving…' : 'Accept'}</button>
                    <button className="btn btn-sm btn-danger" disabled={busy} onClick={() => send(t, 'denied')}>{busy && decide.variables?.decision === 'denied' ? 'Saving…' : 'Deny'}</button>
                    {t.status !== 'open' && <button className="btn btn-sm" disabled={busy} onClick={() => send(t, 'open')}>Put back to open</button>}
                    <button className="btn btn-sm btn-ghost" disabled={busy} onClick={() => setAnswering(null)}>Cancel</button>
                  </div>
                </div>
              ) : t.status === 'open' ? (
                <div className="sr-actions">
                  <button className="btn btn-sm btn-primary" onClick={() => startAnswer(t)}>Accept or deny…</button>
                </div>
              ) : (
                <div className="sr-decided tk-decided">
                  <span className="muted">{TICKET_STATUS_LABEL[t.status]} by {nameOf(t.decidedBy) || t.decidedBy} · {df.dateTime(t.decidedAt, { year: 'auto' })}</span>
                  {t.reply && <span className="tk-reply-text">“{t.reply}”</span>}
                  <button type="button" className="btn btn-sm btn-ghost" onClick={() => startAnswer(t)}>Change</button>
                </div>
              )}
            </article>
          );
        })}
      </div>
    </>
  );
}
